// Types partagés du wizard d'import de classement (ImportClassement.vue + étapes).
// Toutes les requêtes passent par dartsHubApi (backend DartsHub, où vivent /leaderboard,
// /clubs, /seasons, /events).

export type SourceKind = 'manual' | 'excel' | 'dartup' | 'd4w' | 'dartconnect' | '3kdarts' | 'federal_pdf'

export type ScopeLevel = 'comite' | 'ligue' | 'federal'

// Un rep comité/ligue n'importe QUE dans sa propre entité (vérifié aussi côté serveur par
// check_import_scope_access) -- `null` signifie "choix libre", réservé au super_admin.
export interface LockedScope {
  level: 'comite' | 'ligue'
  id: number
  code: string
  name: string
}

export const RANKING_CATEGORIES = [
  { value: 'individuel_mixte', label: 'Individuel Masculin' },
  { value: 'individuel_feminin', label: 'Individuel Féminin' },
  { value: 'individuel_veteran', label: 'Individuel Vétéran' },
  { value: 'individuel_junior', label: 'Individuel Junior' },
  { value: 'double_mixte', label: 'Double' },
  { value: 'double_feminin', label: 'Double Féminin' },
] as const

export interface SourceTypeOption {
  code: string
  label: string
}

export interface ComiteOption {
  id: number
  code: string
  name: string
}

export interface LigueOption {
  id: number
  code: string
  name: string
}

export interface SeasonOption {
  id: number
  label: string
  is_current: boolean
}

export interface EventOption {
  id: number
  name: string
  display_name: string | null // "Open de Comité #2" -- cf. OfficialEvent.display_name
  date: string | null
  place: string | null
  organiser: string | null
  comite: string | null // code comité (ex. "MER") -- pour résoudre scope_id automatiquement
  ligue: string | null // code ligue (ex. "SDE") -- idem
}

// Devine périmètre + type de points depuis le nom du tournoi (mêmes motifs que
// Dartshub/backend/scripts/migrate_leaderboards_json.py::_resolve_slot_event) -- ce n'est
// qu'une PRÉ-SUGGESTION, toujours modifiable ensuite : rien dans les données du tournoi ne
// dit formellement "ceci est un Open de Ligue" vs "Open de Comité", seul le nom le laisse
// deviner. `null` si aucun motif ne correspond -- l'admin choisit alors manuellement.
export function guessScopeAndSourceType(eventName: string): { scopeLevel: ScopeLevel; sourceTypeCode: string } | null {
  const n = eventName.toLowerCase()
  if (n.includes('coupe du comité') || n.includes('coupe de comité')) return { scopeLevel: 'comite', sourceTypeCode: 'CC' }
  if (n.includes('open de comité') || n.includes('open comité') || n.includes('open du comité')) {
    return { scopeLevel: 'comite', sourceTypeCode: 'OC' }
  }
  if (n.includes('coupe de la ligue') || n.includes('coupe de ligue')) return { scopeLevel: 'ligue', sourceTypeCode: 'CL' }
  if (n.includes('open de ligue') || n.includes('open de la ligue') || n.includes('open ligue')) {
    return { scopeLevel: 'ligue', sourceTypeCode: 'OL' }
  }
  return null
}

// Une ligne éditable dans le tableau -- avant enrichissement/pointage (StepRowsInput),
// après enrichissement (StepPreview y ajoute display_name/club_code/points/issue).
export interface ImportRow {
  category: string
  player_name_raw: string
  code_clt: string
  // Règlement FFD (art. 331.100) : pas de points sans avoir « joué et gagné au moins un
  // match » (ex. exempté puis battu d'entrée) -- place gardée, 0 point. Renseigné par la
  // source quand elle connaît les matchs (3K Darts, Darts4Windows), sinon coché à la main.
  no_match_won?: boolean
}

export interface EnrichedRow {
  category: string
  player_name_raw: string
  display_name: string
  club_code: string | null
  is_licensed: boolean
  code_clt: string | null
  points: number
  issue: string | null
  no_match_won: boolean
}

// État complet du wizard, porté par ImportClassement.vue et transmis à chaque étape.
export interface WizardParams {
  source: SourceKind | null
  seasonId: number | null
  scopeLevel: ScopeLevel | null
  scopeId: number | null // comite_id ou ligue_id -- null si scopeLevel === 'federal'
  sourceTypeCode: string | null
  eventId: number | null // obligatoire sauf pour federal_pdf
  tournamentDate: string | null // ISO yyyy-mm-dd
  name: string
  place: string | null
}

export function emptyWizardParams(): WizardParams {
  return {
    source: null,
    seasonId: null,
    scopeLevel: null,
    scopeId: null,
    sourceTypeCode: null,
    eventId: null,
    tournamentDate: null,
    name: '',
    place: null,
  }
}

// cf. Dartshub/backend/modules/api/leaderboards/functions.py::get_code_clt -- même barème
// de positions, porté ici pour calculer code_clt côté client sans aller-retour serveur.
// Paliers alignés sur le tableau FFD officiel (Vainqueur/Finaliste/Demi/Quarts/8èmes/
// 16èmes/32èmes/64èmes/126èmes) -- chaque palier au-delà de 16 a son propre code (cf.
// discussion du 2026-09-20 : "la vérité absolue c'est l'image", plus de fourre-tout "17").
export function getCodeClt(pos: number, mode: 'RR' | 'AB', listType: 'A' | 'B'): string {
  if (mode === 'RR') {
    if (pos === 1) return '1'
    if (pos === 2) return '2'
    if (pos <= 4) return '3'
    if (pos <= 8) return '5'
    if (pos <= 16) return '9'
    if (pos <= 32) return '17'
    if (pos <= 64) return '33'
    if (pos <= 226) return '65'
    return '137'
  }
  if (listType === 'A') {
    if (pos === 1) return 'A1'
    if (pos === 2) return 'A2'
    if (pos <= 4) return 'A3'
    if (pos <= 8) return 'A5'
    if (pos <= 16) return 'A9'
    if (pos <= 32) return 'A17'
    if (pos <= 64) return 'A33'
    if (pos <= 226) return 'A65'
    return 'A137'
  }
  // listType === 'B'
  if (pos === 1) return 'B1'
  if (pos === 2) return 'B2'
  if (pos <= 4) return 'B3' // deux demi-finalistes, comme get_code_clt côté serveur
  if (pos <= 8) return 'B5'
  if (pos <= 16) return 'B9'
  if (pos <= 32) return 'B17' // absents du barème de la saison -> 0 point (cf. import_pipeline)
  if (pos <= 64) return 'B33'
  return ''
}
